import type { PrimaryCollectionArtworksParams } from '#lib/websites/primary/api/getPrimaryCollectionPage.js';
import { PRIMARY_GALLERY_LIMIT } from '#lib/websites/primary/constants/gallery.js';
import type { FilterSections } from '$global/features/filter-drawer/types';
import { getAppliedFilters } from '$global/features/filter-drawer/utils/getAppliedFilters/getAppliedFilters';
import { getAppliedSort } from '$global/features/filter-drawer/utils/getAppliedSort/getAppliedSort';

type Params = NonNullable<PrimaryCollectionArtworksParams>;
type SortKey = NonNullable<Params['sort']>[number];

const SORT_KEYS: SortKey[] = [
	'artwork_code_number',
	'-artwork_code_number',
	'artwork_code',
	'-artwork_code',
	'primary_series.sort',
	'-primary_series.sort',
];

const isSortKey = (value: string): value is SortKey =>
	(SORT_KEYS as string[]).includes(value);

// Every filter section this grid can render is a `Filter_Checkbox_List` of
// `type: 'checkbox'` — no sliders, toggles, tag lists or radios exist on any
// `Primary_Collection.filter` record. `getCheckboxListAttributeVariables`
// renders such a list two ways depending on the list's own `operator` column:
//
//   'equals'    -> { <path>: { _in: [...] } }
//   'icontains' -> { _or: [ { <path>: { _icontains: v } }, ... ] }
//
// Both shapes are read back out of the drawer's own output rather than
// re-derived from the URL, so this inherits the drawer's rules for free — which
// sections count as touched, how select-all behaves, and so on.
interface EqualsTerm {
	artwork_medium?: { _in?: unknown };
	artwork_dimensions?: { _in?: unknown };
	primary_series?: { title?: { _in?: unknown }; id?: { _in?: unknown } };
	artists?: { Artists_id?: { name?: { _in?: unknown } } };
}

interface ContainsBranch {
	artwork_medium?: { _icontains?: unknown };
	generic_artwork?: { title?: { _icontains?: unknown } };
}

interface AppliedTerm extends EqualsTerm {
	_or?: ContainsBranch[];
}

const strings = (value: unknown): string[] =>
	Array.isArray(value)
		? value.filter((entry): entry is string => typeof entry === 'string')
		: [];

const assignIfAny = (
	params: Params,
	key: keyof Params,
	values: string[]
): void => {
	if (!values.length) return;
	(params as Record<string, unknown>)[key] = values;
};

export const getPrimaryGalleryBaseParams = ({
	collectionSlug,
	defaultSort,
}: {
	collectionSlug: string;
	defaultSort: string[];
}): Params => ({
	collectionSlug,
	sort: defaultSort.filter(isSortKey),
	limit: PRIMARY_GALLERY_LIMIT,
	offset: 0,
});

export const getPrimaryGalleryRestParams = ({
	collectionSlug,
	defaultSort,
	filterSections,
	searchParams,
}: {
	collectionSlug: string;
	defaultSort: string[];
	filterSections: FilterSections;
	searchParams: URLSearchParams;
}): Params => {
	const applied = getAppliedFilters({
		searchParams,
		filterSections,
	}) as AppliedTerm[];

	const sort = getAppliedSort({ searchParams, defaultSort });

	const params: Params = {
		...getPrimaryGalleryBaseParams({ collectionSlug, defaultSort }),
		sort: sort.filter(isSortKey),
	};

	applied.forEach((term) => {
		assignIfAny(params, 'mediumIn', strings(term.artwork_medium?._in));
		assignIfAny(params, 'dimensionsIn', strings(term.artwork_dimensions?._in));
		assignIfAny(
			params,
			'seriesTitleIn',
			strings(term.primary_series?.title?._in)
		);
		assignIfAny(params, 'seriesIdIn', strings(term.primary_series?.id?._in));
		assignIfAny(
			params,
			'artistNameIn',
			strings(term.artists?.Artists_id?.name?._in)
		);

		// An `icontains` section arrives as one `_or` whose branches each carry a
		// single value, so the values are collected back out of the branches.
		const branches = term._or ?? [];

		assignIfAny(
			params,
			'mediumContains',
			branches
				.map((branch) => branch.artwork_medium?._icontains)
				.filter((value): value is string => typeof value === 'string')
		);
		assignIfAny(
			params,
			'genericArtworkTitleContains',
			branches
				.map((branch) => branch.generic_artwork?.title?._icontains)
				.filter((value): value is string => typeof value === 'string')
		);
	});

	return params;
};
